import React, { useMemo, useState } from 'react';
import { ArrowRight, CheckCircle2, HelpCircle } from 'lucide-react';
import { CONTACT_EMAIL } from '../mock';
import { pricingTiers } from '../config/pricing';

const questions = [
  {
    id: 'stage',
    label: 'What stage is the issuer program in?',
    options: [
      { value: 'planning', label: 'Planning only / early preparation' },
      { value: 'workspace', label: 'Need issuer workspace and file preparation' },
      { value: 'operational', label: 'Ready to operate a certificate program' },
      { value: 'regulated', label: 'Legal opinion, fiduciary, warrant, or jurisdiction routing required' },
    ],
  },
  {
    id: 'scale',
    label: 'How many active workspaces do you need now?',
    options: [
      { value: 'single', label: 'One workspace' },
      { value: 'bundle', label: 'Up to four workspaces' },
      { value: 'institutional', label: 'More than four / institutional setup' },
    ],
  },
  {
    id: 'controls',
    label: 'What controls are needed?',
    options: [
      { value: 'draft', label: 'Draft configuration and document checklist' },
      { value: 'ledger', label: 'Ledger administration and transfer controls' },
      { value: 'enhanced', label: 'Fiduciary, warrant, or jurisdiction files' },
    ],
  },
];

const initialAnswers = {
  stage: 'workspace',
  scale: 'bundle',
  controls: 'draft',
};

const recommendations = {
  'issuer-desk': {
    reason: 'Best for early preparation before an issuer file is ready.',
    nextStep: 'Use the configuration wizard to prepare the file before moving to an issuer workspace.',
  },
  'issuer-workspace': {
    reason: 'Best for a controlled preparation bundle with up to four issuer workspaces and the core service layer.',
    nextStep: 'Open the workspace bundle and prepare admission, evidence, lane selection, wallet coordination, and draft certificate configuration.',
  },
  'certificate-program': {
    reason: 'Best when the file is ready to operate with ledger administration and transfer controls.',
    nextStep: 'Review the certificate program scope, transfer policy, ledger workflow, and reporting exports.',
  },
  'regulated-program': {
    reason: 'Best when legal-opinion intake, fiduciary files, warrant files, or jurisdiction routing are involved.',
    nextStep: 'Prepare a high-touch review with legal instrument intake and responsible-party mapping.',
  },
  enterprise: {
    reason: 'Best for multi-issuer, institutional, white-label, or custom integration requirements.',
    nextStep: 'Scope the institutional workflow, permissions, reporting, and integration requirements.',
  },
};

const selectTierId = (answers) => {
  if (answers.scale === 'institutional') return 'enterprise';
  if (answers.stage === 'regulated' || answers.controls === 'enhanced') return 'regulated-program';
  if (answers.stage === 'operational' || answers.controls === 'ledger') return 'certificate-program';
  if (answers.stage === 'workspace' || answers.scale === 'bundle') return 'issuer-workspace';
  return 'issuer-desk';
};

const PackageFitQuiz = () => {
  const [answers, setAnswers] = useState(initialAnswers);
  const result = useMemo(() => {
    const tierId = selectTierId(answers);
    const tier = pricingTiers.find((item) => item.id === tierId) || pricingTiers[1];
    return {
      ...tier,
      ...recommendations[tierId],
    };
  }, [answers]);

  const updateAnswer = (id, value) => setAnswers((current) => ({ ...current, [id]: value }));
  const mailSubject = encodeURIComponent(`Tangibl3Net package fit - ${result.name}`);
  const mailBody = encodeURIComponent(
    `Recommended package: ${result.name}\nPrice: ${result.priceRange}\nStage: ${answers.stage}\nScale: ${answers.scale}\nControls: ${answers.controls}`
  );

  return (
    <section id="package-fit" className="mt-14 rounded-2xl border border-border bg-muted/30 p-6 lg:p-8">
      <div className="grid lg:grid-cols-[1fr_0.85fr] gap-8 lg:gap-10 items-start">
        <div>
          <div className="section-label mb-4">Package Fit</div>
          <h3 className="font-display text-3xl font-bold tracking-tight text-foreground">
            Find the right Tangibl3Net package.
          </h3>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            Answer three operational questions. This package selector only recommends a commercial starting point; it does not provide legal, regulatory, tax, custody, trustee, fiduciary, or investment advice.
          </p>

          <div className="mt-7 space-y-5">
            {questions.map((question) => (
              <div key={question.id} className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-start gap-3 mb-4">
                  <HelpCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div className="font-semibold text-foreground">{question.label}</div>
                </div>
                <div className="grid gap-2">
                  {question.options.map((option) => {
                    const active = answers[question.id] === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateAnswer(question.id, option.value)}
                        className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-colors ${active ? 'border-primary bg-primary/10 text-foreground' : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'}`}
                      >
                        <span>{option.label}</span>
                        {active && <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <aside className="lg:sticky lg:top-28 rounded-2xl border border-primary/30 bg-card p-6 shadow-xl shadow-slate-900/5">
          <div className="section-label mb-4">Recommended Package</div>
          <h4 className="font-display text-2xl font-bold text-foreground">{result.name}</h4>
          <div className="mt-3 font-mono text-lg font-bold text-primary">{result.priceRange}</div>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{result.reason}</p>
          <div className="mt-5 rounded-xl border border-border bg-muted/40 p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Next step</div>
            <div className="text-sm text-foreground/85 leading-relaxed">{result.nextStep}</div>
          </div>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=${mailSubject}&body=${mailBody}`}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Request package review <ArrowRight className="w-4 h-4" />
          </a>
        </aside>
      </div>
    </section>
  );
};

export default PackageFitQuiz;
